import { Component, h, State, Prop, Event, EventEmitter, Element } from '@stencil/core';

@Component({
  tag: 'signet-month-year-picker',
  styleUrl: 'signet-month-year-picker.scss',
})
export class SignetMonthYearPicker {
  @Element() el!: HTMLElement;

  @State() private inputValue = '';
  @State() private showYearPicker = false;
  @State() private showMonthPicker = false;

  @Prop() value = '';

  @Event() inputChange: EventEmitter<string>;

  private monthsShortForm: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  private monthsFullForm: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  componentWillLoad() {
    this.inputValue = this.value;
  }

  private selectedYear: number | null = new Date().getFullYear();
  private selectedMonth: number | null = new Date().getMonth();

  syncInputValue() {
    const parts = this.inputValue.split('/');
    if (parts.length === 2) {
      const month = parseInt(parts[0], 10);
      const year = parseInt(parts[1], 10);
      if (!isNaN(month) && !isNaN(year)) {
        this.selectedMonth = month - 1;
        this.selectedYear = year;
      }
    }
  }

  handleChange(e: Event) {
    const input = e.target as HTMLInputElement;
    let numericValue = input.value.replace(/\D/g, '');

    // Ensure the month is between 1 and 12
    if (numericValue.length >= 2) {
      const month = parseInt(numericValue.slice(0, 2), 10);
      if (month < 1 || month > 12) {
        numericValue = '12'; // Set to a valid month (12)
      }
    }

    if (numericValue.length > 2) {
      // Insert "/" after the first two characters
      numericValue = numericValue.slice(0, 2) + '/' + numericValue.slice(2);
    }

    this.inputValue = numericValue; // Update the input value

    // Emit the updated value using the custom event
    this.inputChange.emit(this.inputValue);

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.inputValue,
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.inputValue,
    }));

    // Sync the selected year and month based on the input value
    this.syncInputValue();
  }

  handleKeyPress(e: KeyboardEvent) {
    // Allow numeric characters and the "/" character
    if (!/^\d$|^\+$/.test(e.key)) {
      e.preventDefault();
    }
  }

  toggleYearPicker() {
    this.showYearPicker = !this.showYearPicker;
    this.showMonthPicker = false;

    const grandElement = document.querySelectorAll('signet-month-year-picker');

    setTimeout(() => {
      grandElement.forEach((element) => {
        const selectedYearDoc = element.querySelectorAll('.selected-year');

        selectedYearDoc.forEach((selectedYear) => {
          if (selectedYearDoc) {
            selectedYear.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
          }
        });
      });
    }, 1);

    // Sync the selected year and month based on the input value when the year picker is opened
    this.syncInputValue();
  }

  toggleMonthPicker(year: number) {
    this.selectedYear = year;
    this.showMonthPicker = !this.showMonthPicker;

    // Sync the selected year and month based on the input value when the month picker is opened
    this.syncInputValue();
  }

  selectYear(year: number) {
    this.selectedYear = year;
    this.showYearPicker = false;
    this.showMonthPicker = true;
  }

  selectMonth(month: number) {
    this.selectedMonth = month;
    const selectedMonthLoc = this.selectedMonth + 1;
    this.inputValue = `${selectedMonthLoc.toString().padStart(2, '0')}/${
      this.selectedYear
    }`;
    this.inputChange.emit(this.inputValue);

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.inputValue,
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.inputValue,
    }));

    this.showMonthPicker = false;
  }

  renderYearPicker() {
    const currentYear = new Date().getFullYear();
    const yearsBefore = 100;
    const yearsAfter = 100;
    const startYear = currentYear - yearsBefore;
    // const endYear = currentYear + yearsAfter;
    const years = Array.from(
      { length: yearsBefore + yearsAfter + 1 },
      (_, i) => startYear + i
    );

    return (
      <div class="year-picker">
        <div class="year-container">
          {years.map((year) => (
            <button
              class={this.selectedYear === year ? 'selected-year' : ''}
              onClick={() => this.selectYear(year)}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderMonthPicker() {
    return (
      <div class="month-picker">
        <div class="month-container">
          {this.monthsShortForm.map((monthName, index) => (
            <button
              class={this.selectedMonth === index ? 'selected-month' : ''}
              onClick={() => this.selectMonth(index)}
            >
              {monthName}
            </button>
          ))}
        </div>
      </div>
    );
  }

  render() {
    return (
      <host>
        <div class="signet-month-year-picker-container">
          <div class="input-container">
            <input
              class="input"
              type="text"
              placeholder="MM/YYYY"
              value={this.inputValue}
              onChange={this.handleChange.bind(this)}
              onKeyPress={(e: KeyboardEvent) => this.handleKeyPress(e)}
              maxLength={7}
            />
            <signet-icon
              icon="date_range"
              onClick={() => this.toggleYearPicker()}
            />
          </div>
          {(this.showMonthPicker || this.showYearPicker) && (
            <div class="selected-container">
              <p class="year">{this.selectedYear}</p>
              <p class="month">{this.monthsFullForm[this.selectedMonth]}</p>
            </div>
          )}
          {this.showYearPicker && this.renderYearPicker()}
          {this.showMonthPicker && this.renderMonthPicker()}
        </div>
      </host>
    );
  }
}
